import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { updateFinancialAccount } from '@/services/accountService';
import { toast } from 'sonner';

interface EditFinancialAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account: {
    id: string;
    account_name: string;
    account_type: 'asset' | 'liability' | 'equity';
    is_active?: boolean;
  };
  onSuccess: () => void;
}

export function EditFinancialAccountDialog({
  open,
  onOpenChange,
  account,
  onSuccess
}: EditFinancialAccountDialogProps) {
  const [accountName, setAccountName] = useState(account.account_name);
  const [accountType, setAccountType] = useState<'asset' | 'liability' | 'equity'>(account.account_type);
  const [isActive, setIsActive] = useState(account.is_active ?? true);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!accountName.trim()) {
      toast.error('يرجى إدخال اسم الحساب / Please enter account name');
      return;
    }

    setLoading(true);
    try {
      await updateFinancialAccount(account.id, {
        account_name: accountName,
        account_type: accountType,
        is_active: isActive
      });

      toast.success('تم تحديث الحساب بنجاح / Account updated successfully');
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating account:', error);
      toast.error('فشل تحديث الحساب / Failed to update account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>تعديل الحساب المالي / Edit Financial Account</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="account_name">اسم الحساب / Account Name</Label>
            <Input
              id="account_name"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              placeholder="مثال: الصندوق / Example: Cash"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="account_type">نوع الحساب / Account Type</Label>
            <Select value={accountType} onValueChange={(value: 'asset' | 'liability' | 'equity') => setAccountType(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="asset">أصول / Asset</SelectItem>
                <SelectItem value="liability">التزامات / Liability</SelectItem>
                <SelectItem value="equity">حقوق ملكية / Equity</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="is_active">الحالة / Status</Label>
            <Select value={isActive ? 'active' : 'inactive'} onValueChange={(value) => setIsActive(value === 'active')}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">نشط / Active</SelectItem>
                <SelectItem value="inactive">غير نشط / Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              إلغاء / Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'جاري الحفظ... / Saving...' : 'حفظ / Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
